import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { Toaster as SonnerToaster } from "sonner";
import { useTheme } from "@/hooks/use-theme";

/**
 * App-wide toast host.
 *
 * Runs `unstyled`, which is the only way to actually restyle sonner: its
 * defaults are keyed on `[data-sonner-toast][data-styled='true']`, the same
 * specificity as a class, and they are injected at runtime — so a plain
 * `.app-toast` rule ties and loses on source order. `unstyled` drops the
 * `data-styled` attribute, taking those 18 visual rules with it. The 60 rules
 * that drive positioning, stacking, mount/expand animation and swipe-to-dismiss
 * are not gated on it and still apply.
 *
 * It also frees the close button. Sonner's default pins it half outside the
 * toast (`translate(-35%, -35%)`), which is why it was being clipped; here it
 * sits inside the padding box like any other control.
 *
 * Styles live in `index.css` under `[data-sonner-toast].app-toast`.
 */
export function Toaster() {
  const { theme } = useTheme();

  return (
    <SonnerToaster
      theme={theme}
      position="top-right"
      closeButton
      // Long enough to read a failure tally without chasing it.
      duration={4500}
      visibleToasts={4}
      gap={10}
      icons={{
        success: <CheckCircle2 className="size-[1.05rem]" />,
        error: <XCircle className="size-[1.05rem]" />,
        warning: <AlertTriangle className="size-[1.05rem]" />,
        info: <Info className="size-[1.05rem]" />,
        loading: <Loader2 className="size-[1.05rem] animate-spin" />,
      }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: "app-toast",
          title: "app-toast-title",
          description: "app-toast-description",
          icon: "app-toast-icon",
          content: "app-toast-content",
          closeButton: "app-toast-close",
          actionButton: "app-toast-action",
          cancelButton: "app-toast-cancel",
          success: "app-toast-success",
          error: "app-toast-error",
          warning: "app-toast-warning",
          info: "app-toast-info",
          loading: "app-toast-loading",
        },
      }}
    />
  );
}
